import { useCallback, useMemo, useState } from "react";
import Toast from "../../base/components/Toast";
import { fundsService } from "./fundsService";
import { extractApiErrorMessage } from "./utils";
import {
   PaymentIntentStatus,
   PAYMENT_MESSAGES,
   notifyNonSuccessStatus
} from "./paymentStatus";

/**
 * Encapsulates the saved-card purchase flow and the card-refresh side effects
 * for BuyFundsModal, leaving the component to own only presentational state
 * (which step/mode is showing).
 *
 * Keeps a local copy of the saved card so editing/adding a card updates this
 * modal's view without a parent refresh — which would unmount the modal mid-flow.
 *
 * @param {object}   params
 * @param {string}   params.organizationId
 * @param {object}   params.initialCard          The org's saved card (or null).
 * @param {Promise}  params.stripePromise        Memoized loadStripe() promise.
 * @param {Function} params.onCardActivated      Called after a freshly added card
 *                                               is fetched (switch UI to saved mode).
 * @param {Function} params.onPurchaseSettled    Called once after every purchase
 *                                               outcome (refresh parent + close).
 */
export function useBuyFunds({
   organizationId,
   initialCard,
   stripePromise,
   onCardActivated,
   onPurchaseSettled
}) {
   const [card, setCard] = useState(initialCard);
   const [isBuying, setIsBuying] = useState(false);
   const [isCardProcessing, setIsCardProcessing] = useState(false);

   // A saved card only exists when we actually have a paymentMethodId. The raw
   // card value can be an empty array or {} (both truthy), so never test `card`
   // for truthiness directly.
   const hasSavedCard = Boolean(card?.paymentMethodId);

   // A charge is in flight on either path; closing the modal now would drop the
   // result and fire toasts/state updates after unmount.
   const isPaymentInFlight = isBuying || isCardProcessing;

   // Re-fetch the saved card into local state (avoids a parent refresh that
   // would unmount the modal). `activate` switches the modal to the saved-card
   // view, used after a brand-new card is attached. Processing stays true for
   // the duration so the Buy button can't fire with a stale paymentMethodId.
   const reloadSavedCard = useCallback(
      (activate) => {
         setIsCardProcessing(true);
         // Returns the request promise so callers (AddCardForm) can keep their
         // modal open until the re-fetch actually resolves, not just starts.
         return fundsService.getSavedCard(
            organizationId,
            (res) => {
               setCard(res.data);
               if (activate) onCardActivated?.();
               setIsCardProcessing(false);
            },
            () => setIsCardProcessing(false)
         );
      },
      [organizationId, onCardActivated]
   );

   const refreshSavedCard = useCallback(() => reloadSavedCard(false), [reloadSavedCard]);
   const handleCardAdded = useCallback(() => reloadSavedCard(true), [reloadSavedCard]);

   // Saved-card path: server creates + confirms the PaymentIntent, then the
   // client only runs 3D Secure if the issuer asks for it.
   const buyWithSavedCard = useCallback(
      async (selectedPackage) => {
         if (!card?.paymentMethodId) {
            Toast.warning("Please add a payment method first.");
            return;
         }

         setIsBuying(true);
         try {
            // 1) Server creates + confirms the PaymentIntent with the saved card.
            const res = await fundsService.pay({
               organizationId,
               funds: selectedPackage.funds,
               paymentMethodId: card.paymentMethodId
            });

            let { status } = res.data;
            const { clientSecret } = res.data;
            // paymentIntentId comes from /pay in the no-3DS case; after 3DS we
            // take it from the handleNextAction result instead.
            let paymentIntentId = res.data.paymentIntentId;

            // 2) 3D Secure, only when the issuer requires it.
            if (status === PaymentIntentStatus.REQUIRES_ACTION) {
               if (!clientSecret) {
                  Toast.warning("Could not complete the purchase. Please try again.");
                  return;
               }
               const stripe = await stripePromise;
               const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret });
               if (error) {
                  Toast.warning(error.message ?? "Authentication failed. Please try again.");
                  return;
               }
               status = paymentIntent?.status;
               paymentIntentId = paymentIntent?.id ?? paymentIntentId;
            }

            if (status === PaymentIntentStatus.SUCCEEDED) {
               // 3) Stripe charged the card — tell the backend to finalize. If
               // this fails the money is already taken, so we do NOT show an
               // error: the payment_intent.succeeded webhook grants the funds.
               try {
                  await fundsService.confirmPayment(paymentIntentId);
                  Toast.success(PAYMENT_MESSAGES.success);
               } catch {
                  Toast.success(PAYMENT_MESSAGES.pendingConfirm);
               }
            } else if (!notifyNonSuccessStatus(status)) {
               Toast.warning(PAYMENT_MESSAGES.failed);
            }
         } catch (err) {
            // The reason can sit on the envelope's `message` or, when that's
            // null, in its `errors` array — surface the exact text either way.
            Toast.warning(extractApiErrorMessage(err, "Could not complete the purchase."));
         } finally {
            // Every outcome closes the modal and refreshes the parent. Doing it
            // only here means onPurchaseSettled runs exactly once.
            setIsBuying(false);
            onPurchaseSettled?.();
         }
      },
      [card, organizationId, stripePromise, onPurchaseSettled]
   );

   return useMemo(
      () => ({
         card,
         isBuying,
         isCardProcessing,
         isPaymentInFlight,
         hasSavedCard,
         setIsCardProcessing,
         refreshSavedCard,
         handleCardAdded,
         buyWithSavedCard
      }),
      [
         card,
         isBuying,
         isCardProcessing,
         isPaymentInFlight,
         hasSavedCard,
         refreshSavedCard,
         handleCardAdded,
         buyWithSavedCard
      ]
   );
}
